import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Form,
  Image,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";

import { useAppDispatch } from "../../../store/hooks";
import SpinnerLoader from "../../common/SpinnerLoader";
import { handleSuccess, renderErrorToast } from "../../../utility/helpers";
import { updateJob } from "../../../store/features/dashboard";
import { NUMBERS } from "../../../resources/number-constants";
import { clearStoredDistance, markNeedsRegenerate } from "../../../utility/distancePrefs";
import {
  backBtn,
  editBtn,
  scrollDownArrowIcon,
} from "../../../assets/images/images";
import JDEditField from "./JDEditField";
import JDField from "./JDField";

const JobDataHeading: any = {
  title: "Job Title",
  about: "About",
  alternative_job_title: "Alternate job titles",
  responsibilities: "What are the day-to-day responsibilities of the role?",
  value_plus_goals: "What are the value-plus goals of this position?",
  skills: "Skills",
  industries: "Industries",
  experience: "Years of experience",
  education: "Education",
  certificate: "Licenses & Certifications",
  language: "Spoken Language",
  work_on: "What would you be working on?",
  work_approach: "How do you approach your work?",
  prior_company_name: "Prior company name brand advantage",
  citizenship_work_authorization: "Citizenship / Work authorization",
  security_clearance: "Security clearance",
  location: "Location",
  timezone: "Time Zone",
  distance: "Distance",
  hybrid_schedule: "Hybrid/Flexible Schedule",
  employment_type: "Employment Type",
};

const Detail = ({
  jobData,
  loading,
  refreshJobData,
  jobAction,
  setMShowLoader,
  fetchNPRanking,
  updatedNearPeer,
  handleClose,
}: any) => {
  const dispatch = useAppDispatch();
  const scrollRef = useRef<any>(null);
  const [workMode, setWorkMode] = useState("");
  const [atBottom, setAtBottom] = useState(false);
  const [aboutShow, setAboutShow] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [actionKey, setActionKey] = useState<any>("");
  const [modalData, setModalData] = useState<any>(null);
  const [titleValue, setTitleValue] = useState<any>(null);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
      setAtBottom(true);
    }
  };

  const scrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      setAtBottom(false);
    }
  };

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollTop + clientHeight === scrollHeight) {
      setAtBottom(true);
    } else if (scrollTop === 0) {
      setAtBottom(false);
    }
  };

  const handleEdit = (data: any, key: any) => {
    setActionKey(key);
    setModalData(data);
    if (key === "title") {
      setTitleValue(data);
    } else if (key === "hybrid_schedule") {
      setTitleValue(jobData?.hybrid_schedule?.title[0]);
    } else {
      setTitleValue(null);
    }
    setAboutShow(true);
  };

  const handleUpdateJob = (payload: any, refresh: any) => {
    setShowLoader(true);
    dispatch(updateJob({ payload, id: jobData?.id }))
      .unwrap()
      .then((res: any) => {
        handleSuccess(res?.detail);
        setAboutShow(false);
        setShowLoader(false);
        setMShowLoader(false);
        // Mark that graph needs regeneration due to JD changes
        markNeedsRegenerate(jobData?.id);
        if (refresh) {
          refreshJobData(false);
        }
      })
      .catch((error: any) => {
        setAboutShow(false);
        setShowLoader(false);
        console.info("Error updating content:", error);
      });
  };

  const handleSave = () => {
    if (
      (actionKey === "title" || actionKey === "hybrid_schedule") &&
      titleValue?.trim() === ""
    ) {
      renderErrorToast("Please enter valid content before saving.");
      return;
    }

    if (
      !titleValue &&
      (!modalData || modalData.trim() === "" || modalData === "<p><br></p>"
    || modalData === "<ul></ul>")
    ) {
      renderErrorToast("Please enter valid content before saving.");
      return;
    }

    const payload: any = {
      user: jobData?.user,
      conversation_id: jobData?.conversation_id,
      job_conversation_id: jobData?.job_conversation_id,
    };

    if (actionKey === "about") {
      payload[actionKey] = modalData;
    } else if (actionKey === "title") {
      payload[actionKey] = titleValue;
    } else if (actionKey === "hybrid_schedule") {
      payload[actionKey] = { title: titleValue };
    } else {
      payload[actionKey] = { title: modalData };
    }

    // If distance was edited, clear any previously saved slider preference
    if (actionKey === "distance") {
      clearStoredDistance(jobData?.id);
    }

    handleUpdateJob(payload, true);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    action: any
  ) => {
    setWorkMode(e.target.value);

    const payload: any = {
      user: jobData?.user,
      conversation_id: jobData?.conversation_id,
      job_conversation_id: jobData?.job_conversation_id,
    };

    payload[action] = { title: [e.target.value] };

    setMShowLoader(true);
    handleUpdateJob(payload, true);
  };

  const handleWorkModeText = (workModeData: any, validValues: any) => {
    if (typeof workModeData !== "string") return workModeData;

    const exists = validValues.some(
      (validValue: any) =>
        validValue.toLowerCase() === workModeData.toLowerCase()
    );

    if (!exists) {
      const words = workModeData.trim().split(/\s+/);
      if (words.length > 1) {
        return words.join("-");
      }
    }

    return workModeData;
  };

  // Simplified ranking getter
  const getRanking = useCallback(
    (attribute: any) => {
      return (
        updatedNearPeer?.[`${attribute}_ranking`] ||
        jobData?.[attribute]?.ranking ||
        jobData?.[`${attribute}_ranking`]
      );
    },
    [updatedNearPeer, jobData]
  );

  useEffect(() => {
    if (jobData?.work_mode) {
      const flatValue = jobData?.work_mode?.title?.flat();
      const workModeValue = handleWorkModeText(
        flatValue[0],
        jobData?.work_mode?.options
      );
      setWorkMode(workModeValue);
    }
  }, [jobData]);
  return (
    <>
      {!loading && (
        <div className="outer-section">
          <OverlayTrigger placement="top" overlay={<Tooltip>Back</Tooltip>}>
            <div className="back-btn" onClick={() => handleClose()}>
              <Image
                src={backBtn}
                height={25}
                width={25}
                alt="Back"
                className="back-btn-arrow"
              />
            </div>
          </OverlayTrigger>
        </div>
      )}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="preview-left page-scroll"
      >
        {!loading && (
          <>
            <div className="preview-title d-none d-md-block">
              {jobAction !== "edit" ? jobData?.title : "Edit Job Description"}{" "}
              <span>.</span>
            </div>

            {jobData?.company_name && (
              <div className="badge-container d-none d-md-block">
                <Badge bg="secondary">{jobData?.company_name}</Badge>
              </div>
            )}

            <div
              className={`scroll-down-btn ${atBottom ? "scroll-up" : "scroll-down"}`}
            >
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>{atBottom ? "Go to Top" : "Go to Bottom"}</Tooltip>
                }
              >
                <Button
                  className="scroll-down"
                  onClick={atBottom ? scrollToTop : scrollToBottom}
                >
                  <Image
                    src={scrollDownArrowIcon}
                    height={48}
                    width={48}
                    alt="arrow"
                    className="scroll-down-arrow"
                  />
                </Button>
              </OverlayTrigger>
            </div>

            <div className="scroll-container">
              <div className="content">
                <h2>About</h2>
                <div dangerouslySetInnerHTML={{ __html: jobData?.about }} />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() => handleEdit(jobData?.about, "about")}
                >
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Edit</Tooltip>}
                  >
                    <div className="render-btn-flex">
                      <Image
                        src={editBtn}
                        height={12}
                        width={12}
                        alt="arrow"
                        className="brown-arrow"
                      />
                      <Image
                        src={editBtn}
                        height={12}
                        width={12}
                        alt="arrow"
                        className="white-arrow d-none"
                      />
                    </div>
                  </OverlayTrigger>
                </Button>
              </div>
            </div>

            <div className="header-with-text new-header-text">
              <h2>
                What would you work on? <span className="line"></span>
              </h2>
            </div>

            <JDField
              title={JobDataHeading["title"]}
              field="title"
              nearPeer={getRanking("job_title") || ""}
              renderData={jobData?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["alternative_job_title"]}
              field="alternative_job_title"
              nearPeer={getRanking("alternative_job_title")}
              renderData={jobData?.alternative_job_title?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["responsibilities"]}
              field="responsibilities"
              nearPeer={getRanking("responsibilities")}
              renderData={jobData?.responsibilities?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["value_plus_goals"]}
              field="value_plus_goals"
              nearPeer={getRanking("value_plus_goals")}
              renderData={jobData?.value_plus_goals?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <div className="header-with-text new-header-text">
              <h2>
                What makes you a great candidate? <span className="line"></span>
              </h2>
            </div>

            <JDField
              title={JobDataHeading["skills"]}
              field="skills"
              nearPeer={getRanking("skills")}
              renderData={jobData?.skills?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["industries"]}
              field="industries"
              nearPeer={getRanking("industries")}
              renderData={
                jobData?.industries?.title ||
                "Click the expand icon to update the industries."
              }
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["experience"]}
              field="experience"
              nearPeer={getRanking("experience")}
              renderData={jobData?.experience?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["education"]}
              field="education"
              nearPeer={getRanking("education")}
              renderData={jobData?.education?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["certificate"]}
              field="certificate"
              nearPeer={getRanking("certificate")}
              renderData={
                jobData?.certificate?.title ||
                "Click the expand icon to add the certificates."
              }
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["language"]}
              field="language"
              nearPeer={getRanking("language")}
              renderData={jobData?.language?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["work_on"]}
              field="work_on"
              nearPeer={getRanking("work_on")}
              renderData={jobData?.work_on?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["work_approach"]}
              field="work_approach"
              nearPeer={getRanking("work_approach")}
              renderData={jobData?.work_approach?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["prior_company_name"]}
              field="prior_company_name"
              nearPeer={getRanking("prior_company_name")}
              renderData={jobData?.prior_company_name?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["citizenship_work_authorization"]}
              field="citizenship_work_authorization"
              nearPeer={getRanking("citizenship_work_authorization")}
              renderData={jobData?.citizenship_work_authorization?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["security_clearance"]}
              field="security_clearance"
              nearPeer={getRanking("security_clearance")}
              renderData={jobData?.security_clearance?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <div className="header-with-text new-header-text how-work-text">
              <h2>
                How would you work? <span className="line"></span>
              </h2>
            </div>

            <JDField
              title={JobDataHeading["employment_type"]}
              field="employment_type"
              nearPeer={getRanking("employment_type")}
              renderData={jobData?.employment_type?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Work from</h4>
                  <Button
                    variant="primary"
                    className="regenerate-btn"
                    onClick={fetchNPRanking}
                  >
                    {getRanking("work_mode")
                      ? "Regenerate"
                      : "Generate Near Peer Ranking"}
                  </Button>
                  {getRanking("work_mode") && (
                    <div
                      className={`text ${getRanking("work_mode") > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {getRanking("work_mode")}%
                    </div>
                  )}
                </div>
              </div>

              <div className="employment-options">
                {jobData?.work_mode?.options?.map((mode: any) => (
                  <Form.Check
                    type="radio"
                    id={mode}
                    name="workMode"
                    key={mode}
                    label={mode}
                    value={mode}
                    checked={workMode.toLowerCase() === mode.toLowerCase()}
                    onChange={(e) => handleChange(e, "work_mode")}
                    className="employment-radio"
                  />
                ))}
              </div>
            </div>

            {workMode &&
              (workMode.toLowerCase() === "hybrid" ||
                workMode.toLowerCase() === "flexible") && (
                <JDField
                  title={JobDataHeading["hybrid_schedule"]}
                  field="hybrid_schedule"
                  nearPeer={getRanking("hybrid_schedule")}
                  renderData={
                    jobData?.hybrid_schedule?.title[0] ||
                    "Click the expand icon to update the schedule."
                  }
                  handleEdit={handleEdit}
                  fetchNPRanking={fetchNPRanking}
                  showLoader={showLoader}
                />
              )}

            <JDField
              title={JobDataHeading["location"]}
              field="location"
              nearPeer={getRanking("location")}
              renderData={jobData?.location?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["distance"]}
              field="distance"
              nearPeer={getRanking("distance")}
              renderData={jobData?.distance?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />

            <JDField
              title={JobDataHeading["timezone"]}
              field="timezone"
              nearPeer={getRanking("timezone")}
              renderData={jobData?.timezone?.title}
              handleEdit={handleEdit}
              fetchNPRanking={fetchNPRanking}
              showLoader={showLoader}
            />
          </>
        )}
        {loading && <SpinnerLoader />}
      </div>

      {aboutShow && (
        <JDEditField
          aboutShow={aboutShow}
          setAboutShow={setAboutShow}
          modalHeading={JobDataHeading}
          actionKey={actionKey}
          modalData={modalData}
          setModalData={setModalData}
          titleValue={titleValue}
          setTitleValue={setTitleValue}
          showLoader={showLoader}
          handleSave={handleSave}
        />
      )}
    </>
  );
};

export default Detail;
